import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import {
    FormControl,
    Box,
    Button,
    MenuItem,
    Select,
    SelectChangeEvent,
    Paper,
    InputLabel,
    TextField,
} from '@mui/material';
import { LabClient } from 'models/client-directory';
import { User } from 'models/user';
import { useMutation, useQuery } from '@apollo/client';
import { getTechniciansForUserListTemplate } from 'apollo/templates/user/query.graphql';
import { useTranslation } from 'react-i18next';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { saveAccountManagerTemplate } from 'apollo/templates/client-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type InternalInformationProps = {
    labClient: LabClient;
    onAccountManagerSaved?: () => void;
};
const InternalInformation: FC<InternalInformationProps> = ({
    labClient,
    onAccountManagerSaved,
}: InternalInformationProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const [technicians, setTechnicians] = useState<User[]>([]);
    const [userId, setUserId] = useState(labClient.accountManager);
    const [clinicInternalNote, setClinicInternalNote] = useState('');
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrganization = currentOrgVar();

    const { loading } = useQuery(getTechniciansForUserListTemplate, {
        variables: {
            filters: { active: true, role: 'technician' },
            limit: 0,
            offset: 0,
        },
        onCompleted: ({ technicians }) => {
            setTechnicians(technicians);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        const clinicInternalNote = labClient.customer?.clinicInternalNotes?.find(
            (note) => note.lab === currentOrganization?._id,
        );
        setClinicInternalNote(clinicInternalNote?.note || '');
    }, [labClient.customer?.clinicInternalNotes]);

    const [saveAccountManager] = useMutation(saveAccountManagerTemplate, {
        onCompleted: () => {
            if (onAccountManagerSaved) {
                onAccountManagerSaved();
            }
        },
        onError: (error) => {
            showBoundary(error);
        },
    });

    const handleInternalNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
        setClinicInternalNote(event.target.value);
    };

    const handleUserChange = (event: SelectChangeEvent) => {
        setUserId(event.target.value);
    };

    const handleSaveAccountManager = async () => {
        await saveAccountManager({
            variables: {
                clientId: labClient.customer._id,
                accountManager: userId,
                clinicInternalNote: clinicInternalNote,
            },
        });
    };

    return (
        <Paper>
            <Box p={2}>
                <Box mb={2}>
                    <FormControl margin="dense" fullWidth>
                        <InputLabel>{translate('client-directory:ACCOUNT_MANAGER')}</InputLabel>
                        <Select
                            id="account-manager"
                            label={translate('client-directory:ACCOUNT_MANAGER')}
                            value={userId}
                            onChange={handleUserChange}
                        >
                            {technicians.map((user) => (
                                <MenuItem value={user._id} key={user._id}>
                                    {`${user.name.firstName} ${user.name.lastName}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box mb={2}>
                    <FormControl margin="dense" fullWidth>
                        <TextField
                            id="comments"
                            label={translate('client-directory:CLINIC_INTERNAL_NOTE')}
                            multiline
                            minRows={4}
                            maxRows={10}
                            value={clinicInternalNote}
                            onChange={handleInternalNoteChange}
                        />
                    </FormControl>
                </Box>
                <Box mt={2}>
                    <Button
                        variant="contained"
                        onClick={handleSaveAccountManager}
                        disabled={(!userId && !clinicInternalNote) || loading}
                    >
                        {translate('common:SAVE')}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default InternalInformation;
